import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {StorageService} from "../../services/storage.service";
import {ActivatedRoute, Router} from "@angular/router";
import {getDeepestChildRoute} from "../../utils";
import {BoroughPreference, IncomeOption, RoomPreference} from "../../model/user";
import {UserService} from "../../services/user.service";
import {ExternalCodeService} from "../../services/external-code.service";

export type SignupFormState = Partial<{
  readonly boroughPreferences: BoroughPreference[],
  readonly roomPreferences: RoomPreference[],
  readonly householdDetails: {
    size: number,
    income: IncomeOption,
  },
  readonly referralCode: string | undefined,
  readonly receivedRentalAssistance: boolean | undefined,
}>

@Injectable({
  providedIn: 'root'
})
export class SignupFormService {
  private readonly _formState = signal<SignupFormState | undefined>(undefined)
  public readonly formState = computed(() => this._formState() ?? {})
  public readonly shouldAnimate = signal(true)
  public readonly user = this.userService.user;
  private readonly externalCodeService = inject(ExternalCodeService);
  constructor(
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly userService: UserService,
  ) {
  }

  public readonly loadInitialFormState = async () => {
    if (this._formState() !== undefined) {
      return true;
    }

    const value = await this.storageService.getItem("SIGNUP_FORM_STATE");
    const code = await this.externalCodeService.getSavedCode();
    this._formState.set({
      ...value,
      referralCode: code?.type === 'referral' ? code.code : undefined,
    });
    return true;
  }

  readonly completedFormStateProperties = computed(() => {
    const formState = this.formState();
    if (
      formState.boroughPreferences &&
      formState.boroughPreferences.length > 0 &&
      formState.roomPreferences &&
      formState.roomPreferences.length > 0 &&
      formState.householdDetails &&
      formState.receivedRentalAssistance !== undefined
    ) {
      return {
        householdIncome: formState.householdDetails.income,
        householdSize: formState.householdDetails.size,
        preferences: {
          borough: formState.boroughPreferences,
          room: formState.roomPreferences,
        },
        referralCode: formState.referralCode,
        receivedRentalAssistance: formState.receivedRentalAssistance,
      }
    } else {
      return undefined;
    }
  });

  public readonly formStateComplete = computed(() => {
    return this.completedFormStateProperties() !== undefined;
  });

  public initialize() {
    effect(() => {
      const state = this._formState();
      if (state)
        this.storageService.setItem('SIGNUP_FORM_STATE', state)
    })
  }

  submitBoroughPreferences = async (form: { preferences: BoroughPreference[] }) => {
    this._formState.update(state => ({
      ...state,
      boroughPreferences: form.preferences,
    }));

    await this.router.navigate(['../..', 'rental-assistance'], {relativeTo: getDeepestChildRoute(this.route)})
  }

  submitRoomPreferences = async (form: { preferences: RoomPreference[] }) => {
    this._formState.update(state => ({
      ...state,
      roomPreferences: form.preferences,
    }));
    await this.router.navigate(['..', '2'], {relativeTo: getDeepestChildRoute(this.route)})
  }

  submitHouseholdDetails = async (form: { size: number | undefined, income: IncomeOption }) => {
    const size = form.size;

    if (size === undefined) {
      throw new Error('Household size is required')
    }

    this._formState.update(state => ({
      ...state,
      householdDetails: {
        size: size,
        income: form.income
      },
    }));

    await this.router.navigate(['..', 'eligible-apartments'], {relativeTo: getDeepestChildRoute(this.route)})
  }

  submitRentalAssistance = async (form: { selection: 'yes' | 'no' | null }) => {
    const receivedRentalAssistance = form.selection === 'yes';

    this._formState.update(state => ({
      ...state,
      receivedRentalAssistance,
    }));

    this.shouldAnimate.set(true);

    await this.router.navigate(['..', 'household-details'], {relativeTo: getDeepestChildRoute(this.route)})
  }
}
